import React from 'react';
import PropTypes from 'prop-types';
import { SparkIcon } from '@spark';
import RecordCountInput, {
    formatCount,
} from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/RecordCountInput';
import TextAreaField from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/TextAreaField';
import ExamplesListField, {
    trimmedExamples,
} from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/ExamplesListField';
import AdvancedSettingsRow from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/AdvancedSettingsRow';
import {
    LanguageField,
    TextLengthField,
    ToneStyleField,
} from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/SettingFields';
import { HELP_TEXTS } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/constants';

const DESCRIPTION_PLACEHOLDER =
    'e.g., Use when the user expresses satisfaction, approval, or a positive experience.';

const entityDescriptionPlaceholder = (name) =>
    `e.g., Describe what a \u201c${name}\u201d entity should include.`;

/** Figma 147px, reduced by one third for Label description. */
const LABEL_DESCRIPTION_HEIGHT = 98;
/** Retrieval Agent entity blocks use a 130px description textarea. */
const ENTITY_DESCRIPTION_HEIGHT = 130;

/** Initial per-target config state (Figma advanced defaults: Short + English). */
export const createDefaultTargetConfig = () => ({
    description: '',
    examples: [''],
    exclude: '',
    textLength: 'short',
    languages: ['English'],
    toneStyles: [],
    advancedOpen: false,
});

export const hasLabelExamples = (config) => trimmedExamples(config?.examples).length > 0;

const AdvancedFields = ({ variant, config, patch, readOnly = false }) => {
    if (variant === 'classification') {
        // cls step 2: Exclude + Text length side by side, then Language + Tone.
        return (
            <>
                <div className="gdai-row">
                    <TextAreaField
                        label="Exclude"
                        help={HELP_TEXTS.exclude}
                        placeholder="e.g., Do not generate neutral, mixed, or negative sentiment."
                        value={config.exclude}
                        onChange={patch('exclude')}
                        height={101}
                        readOnly={readOnly}
                    />
                    <TextLengthField
                        value={config.textLength}
                        onChange={patch('textLength')}
                        showOptionHelp
                    />
                </div>
                <div className="gdai-row">
                    <LanguageField value={config.languages} onChange={patch('languages')} />
                    <ToneStyleField value={config.toneStyles} onChange={patch('toneStyles')} />
                </div>
            </>
        );
    }
    // ml / ner step 2: full-width Text length, then Language + Tone (no Exclude).
    return (
        <>
            <TextLengthField
                value={config.textLength}
                onChange={patch('textLength')}
                showOptionHelp
            />
            <div className="gdai-row">
                <LanguageField value={config.languages} onChange={patch('languages')} />
                <ToneStyleField value={config.toneStyles} onChange={patch('toneStyles')} />
            </div>
        </>
    );
};

AdvancedFields.propTypes = {
    variant: PropTypes.oneOf(['classification', 'multiLabel', 'ner']).isRequired,
    config: PropTypes.object.isRequired,
    patch: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
};

const TargetConfigSection = ({
    variant,
    name,
    count,
    config,
    onConfigChange,
    onRemoveTarget,
    examplesError,
    readOnly = false,
}) => {
    const patch = (key) => (value) => onConfigChange(name, { [key]: value });
    const isNer = variant === 'ner';
    const descriptionField = (
        <TextAreaField
            label={isNer ? 'Entity description' : 'Label description'}
            help={isNer ? HELP_TEXTS.entityDescription : HELP_TEXTS.labelDescription}
            placeholder={isNer ? entityDescriptionPlaceholder(name) : DESCRIPTION_PLACEHOLDER}
            value={config.description}
            onChange={patch('description')}
            height={isNer ? ENTITY_DESCRIPTION_HEIGHT : LABEL_DESCRIPTION_HEIGHT}
            readOnly={readOnly}
        />
    );

    return (
        <div className="gdai-section">
            {isNer ? (
                // Retrieval-style entity block: plain name header + remove icon.
                <div className="gdai-label-header">
                    <span className="fs-unmask">{name}</span>
                    {onRemoveTarget && !readOnly ? (
                        <button
                            type="button"
                            className="gdai-icon-button"
                            aria-label={`Remove ${name}`}
                            onClick={() => onRemoveTarget(name)}
                        >
                            <SparkIcon slug="trash" size="small" />
                        </button>
                    ) : null}
                </div>
            ) : (
                <div className="gdai-section-heading fs-unmask">
                    '{name}' Records Configurations{' '}
                    <span className="gdai-section-heading__count">({formatCount(count) || 0})</span>
                </div>
            )}
            {variant === 'classification' ? (
                <>
                    {descriptionField}
                    <ExamplesListField
                        examples={config.examples}
                        onChange={patch('examples')}
                        error={examplesError}
                        readOnly={readOnly}
                    />
                </>
            ) : null}
            {variant === 'multiLabel' && config.advancedOpen ? (
                // ml step 2: advanced fields render ABOVE the label description.
                <AdvancedFields variant={variant} config={config} patch={patch} readOnly={readOnly} />
            ) : null}
            {variant !== 'classification' ? descriptionField : null}
            <AdvancedSettingsRow open={config.advancedOpen} onToggle={patch('advancedOpen')} />
            {variant !== 'multiLabel' && config.advancedOpen ? (
                // cls / ner step 2: advanced fields render BELOW the toggle row.
                <AdvancedFields variant={variant} config={config} patch={patch} readOnly={readOnly} />
            ) : null}
        </div>
    );
};

TargetConfigSection.propTypes = {
    variant: PropTypes.oneOf(['classification', 'multiLabel', 'ner']).isRequired,
    name: PropTypes.string.isRequired,
    count: PropTypes.number,
    config: PropTypes.object.isRequired,
    onConfigChange: PropTypes.func.isRequired,
    onRemoveTarget: PropTypes.func,
    examplesError: PropTypes.bool,
    readOnly: PropTypes.bool,
};

/**
 * Step 2 of the classification / multi-label / NER flows: "Records to
 * generate" counts grid (one input per target; total is the heading sum) followed
 * by a "<Target> Records Configurations (n)" section per label / entity type.
 * `totalRecordsErrorText` (over the 10,000 ceiling / under the floor) renders in
 * red under the grid and marks every count input.
 */
const RecordsConfigStep = ({
    variant,
    targetNames,
    totalRecords,
    totalRecordsErrorText = '',
    counts,
    onCountChange,
    configs,
    onConfigChange,
    onRemoveTarget,
    examplesErrors = {},
    readOnly = false,
}) => (
    <>
        <div className="gdai-section">
            <div className="gdai-section-heading fs-unmask">
                Records to generate{' '}
                <span className="gdai-section-heading__count">({formatCount(totalRecords) || 0})</span>
            </div>
            <div className="gdai-records-row">
                <div className="gdai-records-row__counts">
                    {targetNames.map((name) => (
                        <div key={name} className="gdai-records-row__count">
                            <RecordCountInput
                                label={`'${name}' Records`}
                                help={HELP_TEXTS.recordCount}
                                placeholder="0"
                                value={counts[name]}
                                onChange={(value) => onCountChange(name, value)}
                                danger={Boolean(totalRecordsErrorText)}
                            />
                        </div>
                    ))}
                </div>
                {totalRecordsErrorText ? (
                    <p className="gdai-field-hint gdai-field-hint--error fs-unmask" role="alert">
                        {totalRecordsErrorText}
                    </p>
                ) : null}
            </div>
        </div>
        {targetNames.map((name) => (
            <TargetConfigSection
                key={name}
                variant={variant}
                name={name}
                count={counts[name]}
                config={configs[name] || createDefaultTargetConfig()}
                onConfigChange={onConfigChange}
                onRemoveTarget={onRemoveTarget}
                examplesError={!!examplesErrors[name]}
                readOnly={readOnly}
            />
        ))}
    </>
);

RecordsConfigStep.propTypes = {
    variant: PropTypes.oneOf(['classification', 'multiLabel', 'ner']).isRequired,
    targetNames: PropTypes.arrayOf(PropTypes.string).isRequired,
    totalRecords: PropTypes.number,
    totalRecordsErrorText: PropTypes.string,
    counts: PropTypes.object.isRequired,
    onCountChange: PropTypes.func.isRequired,
    configs: PropTypes.object.isRequired,
    onConfigChange: PropTypes.func.isRequired,
    onRemoveTarget: PropTypes.func,
    examplesErrors: PropTypes.object,
    readOnly: PropTypes.bool,
};

export default RecordsConfigStep;
